import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    EXTENDED_INFO_GROUPS,
    type ExtendedInfoFormState,
} from '../speciesExtendedInfo';

interface SpeciesExtendedInfoFormProps {
    value: ExtendedInfoFormState;
    onChange: (value: ExtendedInfoFormState) => void;
}

export function SpeciesExtendedInfoForm({ value, onChange }: SpeciesExtendedInfoFormProps) {
    const updateGroupField = (groupKey: string, fieldKey: string, fieldValue: string) => {
        onChange({
            ...value,
            groups: {
                ...value.groups,
                [groupKey]: {
                    ...value.groups[groupKey],
                    [fieldKey]: fieldValue,
                },
            },
        });
    };

    return (
        <div className="space-y-4">
            <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                    Nombres alternativos / sinónimos
                </label>
                <input
                    type="text"
                    value={value.alternativeNames}
                    onChange={e => onChange({ ...value, alternativeNames: e.target.value })}
                    placeholder="Atún de aleta azul, Bluefin tuna (separados por coma)"
                    className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">
                    Se guardan en extended_info.alternativeNames. La app los mezcla con los nombres del catálogo.
                </p>
            </div>

            {EXTENDED_INFO_GROUPS.map(group => (
                <details
                    key={group.key}
                    className="rounded-xl border border-border bg-card/30 overflow-hidden group"
                    open={group.key === 'biology' || group.key === 'fishing'}
                >
                    <summary className="flex items-center justify-between gap-2 px-4 py-3 cursor-pointer list-none font-medium text-sm text-foreground hover:bg-muted/30">
                        <span>{group.label}</span>
                        <ChevronDown size={16} className="text-muted-foreground transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="px-4 pb-4 space-y-3 border-t border-border/60">
                        {group.fields.map(field => {
                            const fieldValue = value.groups[group.key]?.[field.key] ?? '';
                            const useTextarea = (field.rows ?? 0) > 1;
                            return (
                                <div key={field.key}>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        {field.label}
                                    </label>
                                    {useTextarea ? (
                                        <textarea
                                            value={fieldValue}
                                            onChange={e => updateGroupField(group.key, field.key, e.target.value)}
                                            rows={field.rows}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm resize-y"
                                        />
                                    ) : (
                                        <input
                                            type="text"
                                            value={fieldValue}
                                            onChange={e => updateGroupField(group.key, field.key, e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border border-border bg-card text-sm"
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </details>
            ))}

            <p className={cn('text-xs text-muted-foreground')}>
                Estos bloques alimentan la ficha de especie en la app (biología, distribución, pesca, etc.).
            </p>
        </div>
    );
}
